<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  isOnPrivateNote: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const assignedAgent = computed({
  get() {
    return currentChat.value?.meta?.assignee;
  },
  set(agent) {
    const agentId = agent ? agent.id : null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value?.id,
      assignee: agent,
      assigneeType: agent ? 'User' : null,
    });
    store.dispatch('assignAgent', {
      conversationId: currentChat.value?.id,
      agentId,
    });
  },
});

const hasMessage = computed(() => props.message !== '');
const isUserTyping = computed(() => hasMessage.value && !props.isOnPrivateNote);
const isUnassigned = computed(() => !assignedAgent.value);
const isAssignedToOtherAgent = computed(
  () => assignedAgent.value?.id !== currentUser.value?.id
);

const showSelfAssignBanner = computed(() => {
  return (
    isUserTyping.value && (isUnassigned.value || isAssignedToOtherAgent.value)
  );
});

const isPendingConversation = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const isAgentBotOwned = computed(
  () => currentChat.value?.meta?.assignee_type === 'AgentBot'
);

const showBotHandoffBanner = computed(() => {
  return isPendingConversation.value && isAgentBotOwned.value;
});

const botAssigneeName = computed(() => {
  if (isAgentBotOwned.value && assignedAgent.value?.name) {
    return assignedAgent.value.name;
  }

  return t('CONVERSATION.BOT_HANDOFF_FALLBACK_ASSIGNEE');
});

const selfAssignConversation = async () => {
  const { avatar_url, ...rest } = currentUser.value || {};
  assignedAgent.value = { ...rest, thumbnail: avatar_url };
};

const needsAssignmentToCurrentUser = computed(() => {
  return isUnassigned.value || isAssignedToOtherAgent.value;
});

const onClickSelfAssign = async () => {
  try {
    await selfAssignConversation();
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  }
};

const reopenConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: currentChat.value?.id,
    status: 'open',
  });
};

const onClickBotHandoff = async () => {
  try {
    const shouldAssignToCurrentUser =
      isAgentBotOwned.value || needsAssignmentToCurrentUser.value;

    await reopenConversation();

    if (shouldAssignToCurrentUser) {
      await selfAssignConversation();
    }

    useAlert(t('CONVERSATION.BOT_HANDOFF_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.BOT_HANDOFF_ERROR'));
  }
};

const conversationLanguage = computed(
  () => currentChat.value?.additional_attributes?.conversation_language
);

const agentPrimaryLanguage = computed(
  () => (currentUser.value?.languages || [])[0]
);

const hasLanguageMismatch = computed(() => {
  if (!conversationLanguage.value || !agentPrimaryLanguage.value) return false;
  const convLang = conversationLanguage.value.toLowerCase().split(/[-_]/)[0];
  const agentLang = agentPrimaryLanguage.value.toLowerCase().split(/[-_]/)[0];
  return convLang !== agentLang;
});

const autoTranslateDisabled = computed(
  () =>
    currentChat.value?.additional_attributes?.auto_translate_disabled === true
);

const showTranslationBanner = computed(
  () =>
    conversationLanguage.value &&
    hasLanguageMismatch.value &&
    !props.isOnPrivateNote
);

const clientLanguageName = computed(
  () =>
    getLanguageName(conversationLanguage.value) || conversationLanguage.value
);

const translationBannerMessage = computed(() => {
  if (autoTranslateDisabled.value) {
    return t('CONVERSATION.TRANSLATION_BANNER.DISABLED', {
      language: clientLanguageName.value,
    });
  }
  return t('CONVERSATION.TRANSLATION_BANNER.ENABLED', {
    language: clientLanguageName.value,
  });
});

const translationActionLabel = computed(() => {
  return autoTranslateDisabled.value
    ? t('CONVERSATION.TRANSLATION_BANNER.ENABLE_ACTION')
    : t('CONVERSATION.TRANSLATION_BANNER.DISABLE_ACTION');
});

const toggleAutoTranslation = async () => {
  try {
    await store.dispatch('toggleAutoTranslation', {
      conversationId: currentChat.value?.id,
      disabled: !autoTranslateDisabled.value,
    });
    useAlert(
      autoTranslateDisabled.value
        ? t('CONVERSATION.TRANSLATION_BANNER.ENABLED_SUCCESS')
        : t('CONVERSATION.TRANSLATION_BANNER.DISABLED_SUCCESS')
    );
  } catch {
    useAlert(t('CONVERSATION.TRANSLATION_BANNER.TOGGLE_ERROR'));
  }
};
</script>

<template>
  <Banner
    v-if="showTranslationBanner"
    action-button-variant="ghost"
    :color-scheme="autoTranslateDisabled ? 'warning' : 'secondary'"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="translationBannerMessage"
    has-action-button
    :action-button-label="translationActionLabel"
    @primary-action="toggleAutoTranslation"
  />
  <Banner
    v-if="showSelfAssignBanner && !showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
    has-action-button
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <Banner
    v-if="showBotHandoffBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mx-2 mb-2 rounded-lg !py-2"
    :banner-message="
      $t('CONVERSATION.BOT_HANDOFF_MESSAGE', {
        assigneeName: botAssigneeName,
      })
    "
    has-action-button
    :action-button-label="$t('CONVERSATION.BOT_HANDOFF_ACTION')"
    @primary-action="onClickBotHandoff"
  />
</template>
